// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAnalysis } from '../contexts/AnalysisContext';
import { cleanMarkdownComponents } from '../components/dashboard/CleanMarkdownComponents';
import {
  TrendingUp, TrendingDown, BarChart3, Brain, Shield, Download,
  Clock, Zap, Target, AlertCircle, CheckCircle, Sparkles, History,
  ChevronRight, Eye, Star, Calendar, ArrowRight, Info, XCircle, RefreshCw, HelpCircle
} from 'lucide-react';

const AgentAnalysisRedesign = () => {
  const { isDark } = useTheme();
  const tooltipRefs = useRef({});

  // Use global analysis context
  const {
    isAnalyzing: loading,
    progress,
    currentStage,
    symbol: globalSymbol,
    reports: globalReports,
    completedReports: globalCompletedReports,
    error: globalError,
    startAnalysis,
    stopAnalysis,
    clearAnalysis,
    clearError
  } = useAnalysis();

  // Local state for UI
  const [symbol, setSymbol] = useState(globalSymbol || '');
  const [selectedReport, setSelectedReport] = useState('summary');
  const [usageInfo, setUsageInfo] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Sync local symbol with global
  useEffect(() => {
    if (globalSymbol && globalSymbol !== symbol) {
      setSymbol(globalSymbol);
    }
  }, [globalSymbol]);

  // Use global reports and error
  const reports = globalReports;
  const completedReports = globalCompletedReports;
  const error = globalError;

  // Load analysis history
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('agentAnalysis_history');
      if (savedHistory) {
        setAnalysisHistory(JSON.parse(savedHistory));
      }
    } catch (err) {
      console.error('Error loading history:', err);
    }
  }, []);

  const reportSections = [
    {
      id: 'market_report',
      label: 'Market Analysis',
      icon: BarChart3,
      color: 'blue',
      description: 'Technical indicators & price trends',
      category: 'Market Intelligence',
      info: 'Analyzes current price action, moving averages (50 & 200 SMA), RSI, MACD, and other technical indicators to identify trends and momentum. Includes real-time price data and market metrics.'
    },
    {
      id: 'sentiment_report',
      label: 'Social Sentiment',
      icon: Sparkles,
      color: 'purple',
      description: 'Community mood & social signals',
      category: 'Market Intelligence',
      info: 'Evaluates social media sentiment, community discussions, and public opinion across platforms to gauge market mood and retail investor behavior.'
    },
    {
      id: 'news_report',
      label: 'News Analysis',
      icon: Zap,
      color: 'yellow',
      description: 'Latest developments & events',
      category: 'Market Intelligence',
      info: 'Aggregates and analyzes recent news, company announcements, regulatory updates, and macroeconomic events that could impact the cryptocurrency.'
    },
    {
      id: 'fundamentals_report',
      label: 'Fundamentals',
      icon: Target,
      color: 'green',
      description: 'Core metrics & tokenomics',
      category: 'Market Intelligence',
      info: 'Examines market capitalization, circulating supply, total supply, trading volume, market dominance, on-chain metrics, and tokenomics to assess the cryptocurrency\'s fundamental value.'
    },
    {
      id: 'bull_case',
      label: 'Bull Case',
      icon: TrendingUp,
      color: 'emerald',
      description: 'Optimistic scenario & upside potential',
      category: 'Market Perspectives',
      info: 'Presents arguments for why the price could increase, highlighting growth potential, competitive advantages, positive indicators, and reasons to be optimistic about the investment.'
    },
    {
      id: 'bear_case',
      label: 'Bear Case',
      icon: TrendingDown,
      color: 'red',
      description: 'Risk factors & downside scenarios',
      category: 'Market Perspectives',
      info: 'Identifies risks, challenges, competitive weaknesses, and negative indicators that could lead to price decline or underperformance.'
    },
    {
      id: 'research_decision',
      label: 'Research Decision',
      icon: Brain,
      color: 'indigo',
      description: 'In-depth research synthesis',
      category: 'Trading Decisions',
      info: 'Synthesizes all market intelligence and both bull & bear perspectives to provide a balanced research-based recommendation (Buy/Hold/Sell) with detailed rationale.'
    },
    {
      id: 'trader_decision',
      label: 'Trader Decision',
      icon: Target,
      color: 'orange',
      description: 'Trading strategy recommendation',
      category: 'Trading Decisions',
      info: 'Provides a practical trading strategy based on the research decision, including entry/exit points, position sizing, and tactical considerations for execution.'
    },
    {
      id: 'risk_analysis',
      label: 'Risk Analysis',
      icon: Shield,
      color: 'amber',
      description: 'Risk assessment & management',
      category: 'Trading Decisions',
      info: 'Evaluates portfolio risk, position sizing, stop-loss levels, and risk-reward ratios from conservative, neutral, and aggressive perspectives to help manage exposure.'
    },
    {
      id: 'final_decision',
      label: 'Final Decision',
      icon: CheckCircle,
      color: 'cyan',
      description: 'Consolidated recommendation',
      category: 'Trading Decisions',
      info: 'The ultimate recommendation combining all AI agent analyses into a single actionable decision (Buy/Hold/Sell) with confidence level and key supporting points.'
    },
  ];

  const [justCompletedReports, setJustCompletedReports] = useState<Set<string>>(new Set());
  const [hoveredReport, setHoveredReport] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const runAnalysis = () => {
    // Use global analysis context to start analysis
    startAnalysis(symbol);
    setSelectedReport('summary');
  };

  const getErrorDetails = (errorMessage) => {
    if (!errorMessage) return null;

    // Parse error message to provide helpful context
    const errorLower = errorMessage.toLowerCase();

    if (errorLower.includes('logged in') || errorLower.includes('authentication') || errorLower.includes('unauthorized')) {
      return {
        title: 'Authentication Required',
        message: 'You must be logged in to use AI Agent analysis.',
        suggestion: 'Please log in to your account and try again.',
        icon: Shield,
        color: 'red'
      };
    }

    if (errorLower.includes('limit') || errorLower.includes('quota') || errorLower.includes('exceeded')) {
      return {
        title: 'Rate Limit Exceeded',
        message: errorMessage,
        suggestion: 'You\'ve reached your analysis limit. Please wait for your quota to reset.',
        icon: Clock,
        color: 'orange'
      };
    }

    if (errorLower.includes('connection') || errorLower.includes('network') || errorLower.includes('closed')) {
      return {
        title: 'Connection Error',
        message: 'Unable to establish connection to the analysis server.',
        suggestion: 'Please check your internet connection and try again. If the problem persists, our servers may be experiencing issues.',
        icon: AlertCircle,
        color: 'red'
      };
    }

    if (errorLower.includes('symbol') || errorLower.includes('invalid') || errorLower.includes('not found')) {
      return {
        title: 'Invalid Symbol',
        message: 'The cryptocurrency symbol you entered could not be found.',
        suggestion: 'Please check the symbol spelling (e.g., BTC, ETH, SOL) and try again. Make sure the cryptocurrency is supported.',
        icon: HelpCircle,
        color: 'yellow'
      };
    }

    if (errorLower.includes('timeout') || errorLower.includes('timed out')) {
      return {
        title: 'Request Timeout',
        message: 'The analysis took too long to complete.',
        suggestion: 'The server may be busy. Please try again in a few moments.',
        icon: Clock,
        color: 'orange'
      };
    }

    // Generic error
    return {
      title: 'Analysis Failed',
      message: errorMessage,
      suggestion: 'An unexpected error occurred. Please try again. If the problem persists, contact support.',
      icon: XCircle,
      color: 'red'
    };
  };

  const extractDecision = (finalDecisionText) => {
    if (!finalDecisionText) return 'HOLD';
    const text = finalDecisionText.toLowerCase();

    // Check for HOLD-related keywords first
    if (text.includes('hold') || text.includes('neutral') || text.includes('wait')) {
      return 'HOLD';
    }

    // Check for negative BUY cases (DON'T BUY, NOT BUY, AVOID BUY, etc.)
    if (text.includes('don\'t buy') || text.includes('not buy') || text.includes('do not buy') ||
        text.includes('avoid buying') || text.includes('no buy') || text.includes('avoid buy')) {
      return 'HOLD';
    }

    // Check for negative SELL cases (DON'T SELL, NOT SELL, AVOID SELL, etc.)
    if (text.includes('don\'t sell') || text.includes('not sell') || text.includes('do not sell') ||
        text.includes('avoid selling') || text.includes('no sell') || text.includes('avoid sell')) {
      return 'HOLD';
    }

    // Look for explicit BUY recommendation patterns
    const buyPatterns = [
      'recommendation: buy',
      'decision: buy',
      'suggest buy',
      'recommend buy',
      'advised to buy',
      'should buy',
      'buy recommendation',
      'final decision is buy',
      'final recommendation is buy'
    ];

    for (const pattern of buyPatterns) {
      if (text.includes(pattern)) {
        return 'BUY';
      }
    }

    // Look for explicit SELL recommendation patterns
    const sellPatterns = [
      'recommendation: sell',
      'decision: sell',
      'suggest sell',
      'recommend sell',
      'advised to sell',
      'should sell',
      'sell recommendation',
      'final decision is sell',
      'final recommendation is sell'
    ];

    for (const pattern of sellPatterns) {
      if (text.includes(pattern)) {
        return 'SELL';
      }
    }

    // Last resort: check for standalone buy/sell keywords
    // But be cautious - only if they appear in specific contexts
    const buyIndex = text.indexOf('buy');
    const sellIndex = text.indexOf('sell');

    // If both appear, choose the one that appears first in a recommendation context
    if (buyIndex !== -1 && sellIndex !== -1) {
      // Check which one appears in a stronger recommendation context
      const beforeBuy = text.substring(Math.max(0, buyIndex - 20), buyIndex);
      const beforeSell = text.substring(Math.max(0, sellIndex - 20), sellIndex);

      if (beforeBuy.includes('recommend') || beforeBuy.includes('suggest') || beforeBuy.includes('decision')) {
        return 'BUY';
      }
      if (beforeSell.includes('recommend') || beforeSell.includes('suggest') || beforeSell.includes('decision')) {
        return 'SELL';
      }
    }

    // If only one appears
    if (buyIndex !== -1) return 'BUY';
    if (sellIndex !== -1) return 'SELL';

    return 'HOLD';
  };

  const downloadPDF = async () => {
    if (!reports || !symbol) {
      console.error('No analysis data available to download');
      return;
    }

    setDownloadingPDF(true);

    try {
      const token = localStorage.getItem('access_token');
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await axios.post(
        `${apiBase}/api/agent/download-pdf`,
        {
          symbol: symbol,
          date: new Date().toISOString().split('T')[0],
          reports: reports
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `AI_Analysis_${symbol.toUpperCase()}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      alert(`Failed to download PDF: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Scroll to top when switching between reports
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedReport]);

  const getProgressColor = () => {
    if (progress < 25) return 'from-red-500 to-orange-500';
    if (progress < 50) return 'from-orange-500 to-yellow-500';
    if (progress < 75) return 'from-yellow-500 to-blue-500';
    return 'from-blue-500 to-green-500';
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600 group-hover:from-blue-600 group-hover:to-blue-700',
      purple: 'from-purple-500 to-purple-600 group-hover:from-purple-600 group-hover:to-purple-700',
      yellow: 'from-yellow-500 to-yellow-600 group-hover:from-yellow-600 group-hover:to-yellow-700',
      green: 'from-green-500 to-green-600 group-hover:from-green-600 group-hover:to-green-700',
      emerald: 'from-emerald-500 to-emerald-600 group-hover:from-emerald-600 group-hover:to-emerald-700',
      red: 'from-red-500 to-red-600 group-hover:from-red-600 group-hover:to-red-700',
      indigo: 'from-indigo-500 to-indigo-600 group-hover:from-indigo-600 group-hover:to-indigo-700',
      orange: 'from-orange-500 to-orange-600 group-hover:from-orange-600 group-hover:to-orange-700',
      amber: 'from-amber-500 to-amber-600 group-hover:from-amber-600 group-hover:to-amber-700',
      cyan: 'from-cyan-500 to-cyan-600 group-hover:from-cyan-600 group-hover:to-cyan-700'
    };
    return colors[color] || colors.blue;
  };

  const renderSummaryView = () => {
    if (!reports) return null;

    // Only show decision if analysis is complete (not loading) and final_decision exists
    const analysisComplete = !loading && reports.final_decision;

    return (
      <div className="space-y-6">
        {/* Decision Card */}
        <div className={`relative overflow-hidden rounded-2xl ${
          isDark ? 'bg-gradient-to-br from-zinc-800 to-zinc-900' : 'bg-gradient-to-br from-white to-gray-50'
        } border ${isDark ? 'border-white/10' : 'border-gray-200'} p-8 shadow-2xl`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/20 to-purple-500/20 blur-3xl rounded-full"></div>

          <div className="relative">
            <div className="mb-6">
              <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {symbol.toUpperCase()} Analysis
              </h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {analysisComplete ? `Completed ${new Date().toLocaleDateString()}` : 'Analysis in progress...'}
              </p>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Reports</div>
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {completedReports.size}/10
                </div>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Analysis Time</div>
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  ~4 min
                </div>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>AI Agents</div>
                <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  10+
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Report Cards Section */}
        <div>
          <div className="mb-6">
            <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              AI Agent Reports
            </h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Click on any report to view detailed analysis from our AI agents
            </p>
          </div>
          {renderReportCards()}
        </div>
      </div>
    );
  };

  const renderReportCards = () => {
    const categories = ['Market Intelligence', 'Market Perspectives', 'Trading Decisions'];

    // Category icons and colors
    const categoryMeta = {
      'Market Intelligence': { icon: BarChart3, gradient: 'from-blue-500 to-cyan-500' },
      'Market Perspectives': { icon: TrendingUp, gradient: 'from-emerald-500 to-teal-500' },
      'Trading Decisions': { icon: Target, gradient: 'from-orange-500 to-amber-500' }
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {categories.map(category => {
          const categoryReports = reportSections.filter(r => r.category === category);
          const completed = categoryReports.filter(r => completedReports.has(r.id)).length;
          const CategoryIcon = categoryMeta[category].icon;
          const progress = (completed / categoryReports.length) * 100;

          return (
            <div key={category} className="space-y-4">
              {/* Enhanced Category Header */}
              <div className={`relative overflow-hidden rounded-2xl ${
                isDark ? 'bg-zinc-800/50 border border-white/10' : 'bg-white border border-gray-200'
              } shadow-lg`}>
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${categoryMeta[category].gradient} opacity-5`}></div>

                <div className="relative p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2.5 rounded-xl bg-gradient-to-br ${categoryMeta[category].gradient} text-white shadow-lg`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {category}
                      </h3>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {completed}/{categoryReports.length} reports completed
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className={`w-full h-2 rounded-full overflow-hidden ${
                    isDark ? 'bg-white/5' : 'bg-gray-100'
                  }`}>
                    <div
                      className={`h-full bg-gradient-to-r ${categoryMeta[category].gradient} transition-all duration-500 ease-out`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Enhanced Report Cards */}
              <div className="space-y-4">
                {categoryReports.map(report => {
                  const Icon = report.icon;
                  const isCompleted = completedReports.has(report.id);
                  const isLoading = loading && !isCompleted;
                  const justCompleted = justCompletedReports.has(report.id);

                  return (
                    <div
                      key={report.id}
                      onClick={() => {
                        if (isCompleted) {
                          setSelectedReport(report.id);
                        }
                      }}
                      className={`group relative rounded-2xl ${
                        isDark ? 'bg-zinc-800/50' : 'bg-white'
                      } border-2 ${
                        selectedReport === report.id
                          ? `border-${report.color}-500 shadow-xl shadow-${report.color}-500/20`
                          : isDark ? 'border-white/10' : 'border-gray-200'
                      } shadow-lg hover:shadow-2xl transition-all duration-300 ${
                        isCompleted ? 'cursor-pointer transform hover:scale-[1.03] hover:-translate-y-1' : 'opacity-60'
                      } ${justCompleted ? 'animate-pulse-once' : ''}`}
                      style={{
                        animation: justCompleted ? 'pulse-glow 0.6s ease-out' : undefined,
                        overflow: 'visible'
                      }}
                    >
                      {/* Overflow container for gradient effects */}
                      <div className="absolute inset-0 overflow-hidden rounded-2xl">
                        {/* Enhanced gradient overlay */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${getColorClasses(report.color)} ${
                          isCompleted ? 'opacity-0 group-hover:opacity-[0.15]' : 'opacity-[0.03]'
                        } transition-opacity duration-300`}></div>

                        {/* Success flash animation when just completed */}
                        {justCompleted && (
                          <div className={`absolute inset-0 bg-gradient-to-br ${getColorClasses(report.color)} animate-[flash_0.6s_ease-out]`}></div>
                        )}

                        {/* Shimmer effect for loading */}
                        {isLoading && (
                          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                        )}
                      </div>

                      <div className="relative p-6">
                        {/* Icon header with status */}
                        <div className="flex items-start justify-between mb-4">
                          <div className={`p-3.5 rounded-xl bg-gradient-to-br ${getColorClasses(report.color)} text-white shadow-lg transform transition-transform group-hover:scale-110`}>
                            <Icon className="w-6 h-6" />
                          </div>

                          {/* Status indicator */}
                          <div className="flex flex-col items-end gap-1">
                            {isLoading && (
                              <>
                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                                <span className="text-xs text-blue-500 font-medium">Processing...</span>
                              </>
                            )}
                            {isCompleted && (
                              <>
                                <div className="p-1 rounded-full bg-green-500/20">
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                </div>
                                <span className="text-xs text-green-500 font-medium">Ready</span>
                              </>
                            )}
                            {!isCompleted && !isLoading && (
                              <>
                                <div className={`w-5 h-5 rounded-full border-2 ${
                                  isDark ? 'border-white/20' : 'border-gray-300'
                                }`}></div>
                                <span className={`text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Pending
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Content with better spacing */}
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {report.label}
                            </h3>
                            {/* Info Icon with Tooltip */}
                            <div className="relative">
                              <Info
                                ref={(el) => (tooltipRefs.current[report.id] = el)}
                                className={`w-4 h-4 cursor-help ${isDark ? 'text-gray-400 hover:text-blue-400' : 'text-gray-500 hover:text-blue-500'} transition-colors`}
                                onMouseEnter={(e) => {
                                  setHoveredReport(report.id);
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTooltipPosition({
                                    top: rect.top,
                                    left: rect.right + 8
                                  });
                                }}
                                onMouseLeave={() => setHoveredReport(null)}
                              />
                            </div>
                          </div>
                          <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {report.description}
                          </p>

                          {/* Show report preview when completed */}
                          {isCompleted && reports[report.id] && (
                            <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                              <div className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'} line-clamp-4`}>
                                {reports[report.id].substring(0, 300)}...
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action footer */}
                        <div className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                          {isCompleted && (
                            <div className={`flex items-center justify-between text-sm font-medium transition-all ${
                              selectedReport === report.id
                                ? `text-${report.color}-500`
                                : isDark ? 'text-blue-400' : 'text-blue-600'
                            }`}>
                              <span className="group-hover:translate-x-1 transition-transform">View Full Report</span>
                              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          )}
                          {isLoading && (
                            <div className="flex items-center gap-2">
                              <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                                <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 animate-[loading_1.5s_ease-in-out_infinite]" style={{width: '60%'}}></div>
                              </div>
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                AI analyzing...
                              </span>
                            </div>
                          )}
                          {!isCompleted && !isLoading && (
                            <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} text-center`}>
                              Waiting for analysis to start
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDetailedView = () => {
    if (!reports || selectedReport === 'summary') return null;

    const report = reportSections.find(r => r.id === selectedReport);
    if (!report) return null;

    const Icon = report.icon;
    const content = reports[selectedReport];

    return (
      <div className="space-y-6">
        {/* Back Button */}
        <button
          onClick={() => setSelectedReport('summary')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            isDark
              ? 'bg-zinc-800 hover:bg-zinc-700 text-white border border-white/10'
              : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-200'
          }`}
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          <span>Back to All Reports</span>
        </button>

        {/* Report Header */}
        <div className={`rounded-2xl overflow-hidden ${
          isDark ? 'bg-zinc-800/50' : 'bg-white'
        } border ${isDark ? 'border-white/10' : 'border-gray-200'} shadow-lg`}>
          <div className={`p-6 border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${getColorClasses(report.color)} text-white`}>
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {report.label}
                </h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {report.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Clean Document-Style Content */}
        {content ? (
          <div className={`rounded-2xl overflow-hidden p-8 ${
            isDark ? 'bg-zinc-800/50 text-gray-200' : 'bg-white text-gray-900'
          } border ${isDark ? 'border-white/10' : 'border-gray-200'} shadow-lg`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={cleanMarkdownComponents}
            >
              {content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className={`rounded-2xl overflow-hidden ${
            isDark ? 'bg-zinc-800/50' : 'bg-white'
          } border ${isDark ? 'border-white/10' : 'border-gray-200'} shadow-lg`}>
            <div className="text-center py-16">
              <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                No data available for this section
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      {/* Add CSS animations */}
      <style>{`
        @keyframes flash {
          0% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.1;
          }
          100% {
            opacity: 0;
          }
        }

        @keyframes pulse-glow {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 0 20px 10px rgba(34, 197, 94, 0);
          }
        }
      `}</style>

      <div className={`min-h-screen ${isDark ? 'bg-zinc-900' : 'bg-gray-50'}`}>
        {/* Hero Section - Only show when viewing summary/cards, not detailed reports */}
        {selectedReport === 'summary' && (
          <div className="relative overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 opacity-10"></div>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzAwMCIgb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 mb-4 sm:mb-6">
                <Sparkles className="w-3 sm:w-4 h-3 sm:h-4 text-blue-500" />
                <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                  Powered by 10+ AI Agents
                </span>
              </div>

              <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  AI-Powered
                </span>
                <br />
                Trading Intelligence
              </h1>

              <p className={`text-base sm:text-lg lg:text-xl max-w-2xl mx-auto px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Get comprehensive multi-agent analysis combining technical indicators, sentiment,
                news, and risk assessment in minutes
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-3xl mx-auto">
              <div className={`relative rounded-2xl overflow-hidden ${
                isDark ? 'bg-zinc-800/50' : 'bg-white'
              } border ${isDark ? 'border-white/10' : 'border-gray-200'} shadow-2xl backdrop-blur-xl`}>
                <div className="p-6">
                  {/* Usage Info Banner */}
                  {usageInfo && !usageInfo.is_admin && (
                    <div className={`mb-4 p-4 rounded-xl ${
                      usageInfo.calls_remaining <= 1
                        ? (isDark ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-yellow-50 border border-yellow-200')
                        : (isDark ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200')
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          usageInfo.calls_remaining <= 1 ? 'bg-yellow-500/20' : 'bg-green-500/20'
                        }`}>
                          <Zap className={`w-5 h-5 ${
                            usageInfo.calls_remaining <= 1 ? 'text-yellow-500' : 'text-green-500'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            usageInfo.calls_remaining <= 1
                              ? (isDark ? 'text-yellow-400' : 'text-yellow-700')
                              : (isDark ? 'text-green-400' : 'text-green-700')
                          }`}>
                            {usageInfo.calls_remaining > 0
                              ? `${usageInfo.calls_remaining} ${usageInfo.calls_remaining === 1 ? 'analysis' : 'analyses'} remaining today`
                              : 'Daily limit reached - Resets in 24h'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {usageInfo && usageInfo.is_admin && (
                    <div className={`mb-4 p-4 rounded-xl ${
                      isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-200'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-500/20">
                          <Star className="w-5 h-5 text-purple-500" />
                        </div>
                        <p className={`text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
                          Admin Access - Unlimited Analyses
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <input
                      type="text"
                      value={symbol}
                      onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                      placeholder="Enter symbol (BTC, ETH, SOL...)"
                      className={`flex-1 px-4 sm:px-6 py-3 sm:py-4 rounded-xl border ${
                        isDark
                          ? 'bg-zinc-700/50 border-zinc-600 text-white placeholder-gray-400'
                          : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                      onKeyPress={(e) => e.key === 'Enter' && runAnalysis()}
                      disabled={loading}
                    />
                    <button
                      onClick={runAnalysis}
                      disabled={loading}
                      className={`px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all flex items-center justify-center gap-2 sm:gap-3 w-full sm:w-auto ${
                        loading
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                      }`}
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 sm:h-5 w-4 sm:w-5 border-b-2 border-white"></div>
                          <span>Analyzing</span>
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 sm:w-5 h-4 sm:h-5" />
                          <span>Analyze</span>
                        </>
                      )}
                    </button>
                  </div>

                  {error && (() => {
                    const errorDetails = getErrorDetails(error);
                    const ErrorIcon = errorDetails?.icon || AlertCircle;
                    const colorClass = errorDetails?.color || 'red';

                    const colorMap = {
                      red: {
                        bg: isDark ? 'bg-red-500/10' : 'bg-red-50',
                        border: isDark ? 'border-red-500/20' : 'border-red-200',
                        text: isDark ? 'text-red-400' : 'text-red-700',
                        icon: 'text-red-500',
                        button: 'bg-red-500 hover:bg-red-600'
                      },
                      orange: {
                        bg: isDark ? 'bg-orange-500/10' : 'bg-orange-50',
                        border: isDark ? 'border-orange-500/20' : 'border-orange-200',
                        text: isDark ? 'text-orange-400' : 'text-orange-700',
                        icon: 'text-orange-500',
                        button: 'bg-orange-500 hover:bg-orange-600'
                      },
                      yellow: {
                        bg: isDark ? 'bg-yellow-500/10' : 'bg-yellow-50',
                        border: isDark ? 'border-yellow-500/20' : 'border-yellow-200',
                        text: isDark ? 'text-yellow-400' : 'text-yellow-700',
                        icon: 'text-yellow-500',
                        button: 'bg-yellow-500 hover:bg-yellow-600'
                      }
                    };

                    const colors = colorMap[colorClass] || colorMap.red;

                    return (
                      <div className={`mt-4 p-5 rounded-xl ${colors.bg} border ${colors.border} animate-in slide-in-from-top-2 duration-300`}>
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${colors.bg} flex-shrink-0`}>
                            <ErrorIcon className={`w-5 h-5 ${colors.icon}`} />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h4 className={`font-semibold ${colors.text} mb-1`}>
                              {errorDetails?.title || 'Error'}
                            </h4>
                            <p className={`text-sm ${colors.text} mb-2 opacity-90`}>
                              {errorDetails?.message || error}
                            </p>
                            {errorDetails?.suggestion && (
                              <p className={`text-xs ${colors.text} opacity-75 flex items-start gap-2`}>
                                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>{errorDetails.suggestion}</span>
                              </p>
                            )}
                          </div>

                          <button
                            onClick={() => {
                              clearError();
                              if (symbol) runAnalysis();
                            }}
                            className={`${colors.button} text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 hover:scale-105 flex-shrink-0`}
                          >
                            <RefreshCw className="w-4 h-4" />
                            Retry
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Analysis Info Banner - Show when loading */}
                  {loading && (
                    <div className={`mt-4 p-4 rounded-xl ${
                      isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'
                    } flex items-start gap-3`}>
                      <Brain className={`w-5 h-5 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <div>
                        <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                          Deep AI Analysis in Progress
                        </p>
                        <p className={`text-xs leading-relaxed ${isDark ? 'text-blue-300/80' : 'text-blue-600/80'}`}>
                          Our 10 AI agents are conducting comprehensive analysis. This takes approximately <strong>3-4 minutes</strong> to complete all reports.
                          You can read each report as soon as it's ready - no need to wait for everything to finish!
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Progress Bar */}
                  {loading && (
                    <div className="mt-6">
                      <div className="flex justify-between items-center mb-3">
                        <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {currentStage || 'Initializing AI agents...'}
                        </span>
                        <span className={`text-sm font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {completedReports.size}/10 reports
                        </span>
                      </div>
                      <div className={`h-3 rounded-full overflow-hidden ${isDark ? 'bg-zinc-700' : 'bg-gray-200'}`}>
                        <div
                          className={`h-full transition-all duration-500 bg-gradient-to-r ${getProgressColor()}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                            {completedReports.size} of 10 reports completed
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-purple-500" />
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                            ~3-4 minutes total
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions - Only show when analysis is complete */}
            {reports && !loading && Object.keys(reports).length > 0 && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mt-4 sm:mt-6 px-4 sm:px-0">
                <button
                  onClick={() => {
                    if (selectedReport === 'summary') {
                      // Switch to detailed view - select first completed report
                      const firstCompleted = reportSections.find(r => completedReports.has(r.id));
                      if (firstCompleted) setSelectedReport(firstCompleted.id);
                    } else {
                      // Switch back to card view
                      setSelectedReport('summary');
                    }
                  }}
                  className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all ${
                    isDark
                      ? 'bg-zinc-800 hover:bg-zinc-700 text-white border border-white/10'
                      : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-200'
                  } shadow-lg`}
                >
                  <Eye className="w-4 sm:w-5 h-4 sm:h-5" />
                  <span className="text-sm sm:text-base">{selectedReport === 'summary' ? 'Detailed View' : 'Card View'}</span>
                </button>

                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all ${
                    isDark
                      ? 'bg-zinc-800 hover:bg-zinc-700 text-white border border-white/10'
                      : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-200'
                  } shadow-lg`}
                >
                  <History className="w-4 sm:w-5 h-4 sm:h-5" />
                  <span className="text-sm sm:text-base">History ({analysisHistory.length})</span>
                </button>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* History Sidebar */}
          {showHistory && analysisHistory.length > 0 && (
            <div className={`mb-8 rounded-2xl overflow-hidden ${
              isDark ? 'bg-zinc-800/50' : 'bg-white'
            } border ${isDark ? 'border-white/10' : 'border-gray-200'} shadow-lg`}>
              <div className={`p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Recent Analyses
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {analysisHistory.map((item, index) => {
                    const decisionColor = item.decision === 'BUY' ? 'green' : item.decision === 'SELL' ? 'red' : 'yellow';

                    return (
                      <div
                        key={index}
                        className={`p-4 rounded-xl ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'} cursor-pointer transition-all border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {item.symbol}
                            </span>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                            decisionColor === 'green' ? 'bg-green-500' :
                            decisionColor === 'red' ? 'bg-red-500' : 'bg-yellow-500'
                          }`}>
                            {item.decision}
                          </span>
                        </div>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {new Date(item.date).toLocaleDateString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Results Display */}
          {(reports || loading) && (
            <div id="results-section">
              {/* View Mode Toggle with Download PDF */}
              <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className={`inline-flex rounded-xl p-1 ${isDark ? 'bg-zinc-800' : 'bg-gray-200'}`}>
                  <button
                    onClick={() => setSelectedReport('summary')}
                    className={`px-6 py-3 rounded-lg font-medium transition-all ${
                      selectedReport === 'summary'
                        ? isDark
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-white text-gray-900 shadow-lg'
                        : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Summary
                  </button>
                  <button
                    onClick={() => {
                      const firstCompleted = reportSections.find(r => completedReports.has(r.id));
                      if (firstCompleted) setSelectedReport(firstCompleted.id);
                    }}
                    className={`px-6 py-3 rounded-lg font-medium transition-all ${
                      selectedReport !== 'summary'
                        ? isDark
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-white text-gray-900 shadow-lg'
                        : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Detailed Reports
                  </button>
                </div>

                {/* Download PDF Button */}
                {reports && !loading && Object.keys(reports).length > 0 && (
                  <button
                    onClick={downloadPDF}
                    disabled={downloadingPDF}
                    className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all ${
                      isDark
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                        : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white'
                    } shadow-lg hover:shadow-xl ${downloadingPDF ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {downloadingPDF ? (
                      <>
                        <div className="animate-spin rounded-full h-4 sm:h-5 w-4 sm:w-5 border-b-2 border-white"></div>
                        <span className="text-sm sm:text-base font-semibold">Generating...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 sm:w-5 h-4 sm:h-5" />
                        <span className="text-sm sm:text-base font-semibold">Download PDF</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Content Area */}
              {selectedReport === 'summary' ? renderSummaryView() : renderDetailedView()}
            </div>
          )}

          {/* Empty State */}
          {!reports && !loading && (
            <div className="text-center py-24">
              <div className="inline-flex p-6 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 mb-6">
                <Brain className={`w-16 h-16 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Ready to Get Started?
              </h2>
              <p className={`text-lg max-w-md mx-auto mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Enter a cryptocurrency symbol above to get comprehensive AI-powered analysis
              </p>

              {/* Feature Highlights */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
                {[
                  {
                    icon: Zap,
                    title: 'Lightning Fast',
                    description: 'Get comprehensive analysis in 3-4 minutes'
                  },
                  {
                    icon: Brain,
                    title: 'Multi-Agent AI',
                    description: '10+ specialized agents working together'
                  },
                  {
                    icon: Shield,
                    title: 'Risk Analysis',
                    description: 'Comprehensive risk assessment included'
                  }
                ].map((feature, i) => {
                  const Icon = feature.icon;
                  return (
                    <div key={i} className={`p-6 rounded-2xl ${
                      isDark ? 'bg-zinc-800/50' : 'bg-white'
                    } border ${isDark ? 'border-white/10' : 'border-gray-200'} shadow-lg`}>
                      <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-white mb-4">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {feature.title}
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {feature.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Tooltip Portal */}
      {hoveredReport && (
        <div
          className={`fixed w-72 sm:w-80 p-4 rounded-xl shadow-2xl border pointer-events-none ${
            isDark
              ? 'border-white/20 text-gray-200'
              : 'border-gray-300 text-gray-700'
          } text-xs leading-relaxed`}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            zIndex: 99999,
            backgroundColor: isDark ? '#18181b' : '#ffffff'
          }}
        >
          <div
            className={`absolute -left-1 top-3 w-2 h-2 rotate-45 border-l border-b ${
              isDark ? 'border-white/20' : 'border-gray-300'
            }`}
            style={{
              backgroundColor: isDark ? '#18181b' : '#ffffff'
            }}
          ></div>
          {reportSections.find(r => r.id === hoveredReport)?.info}
        </div>
      )}
    </Layout>
  );
};

export default AgentAnalysisRedesign;
